/**
 * Timezone handling, built on the platform Intl API rather than a date library.
 *
 * Cross-border care means the patient and the specialist are routinely in
 * different countries and often on different sides of a DST boundary. The rule
 * everywhere in this codebase: appointments are stored as UTC ISO-8601 and
 * rendered in the viewer's own zone at the last possible moment. Nothing is
 * ever stored in local time.
 */
const UTC_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
export function isUtcIso(value) {
    return typeof value === "string" && UTC_ISO_PATTERN.test(value) && !Number.isNaN(Date.parse(value));
}
/**
 * Normalises any parseable instant to the canonical stored form. Sort keys in
 * DynamoDB depend on this being byte-comparable, so the millisecond field is
 * always present and the offset is always Z.
 */
export function toUtcIso(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new RangeError(`Not a valid instant: ${String(value)}`);
    }
    return date.toISOString();
}
export function isValidTimeZone(timeZone) {
    if (timeZone.trim() === "")
        return false;
    try {
        new Intl.DateTimeFormat("en-US", { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
/** The viewer's own zone, for defaulting `custom:timezone` at signup. */
export function detectTimeZone(fallback = "UTC") {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
    }
    catch {
        return fallback;
    }
}
const PART_KEYS = ["year", "month", "day", "hour", "minute", "second"];
function partsInZone(date, timeZone) {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
    const found = Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
    const result = {};
    for (const key of PART_KEYS) {
        // hourCycle h23 vs h24: some engines render midnight as "24" rather than
        // "00", which would push the computed instant a whole day out.
        const raw = Number(found[key] ?? 0);
        result[key] = key === "hour" && raw === 24 ? 0 : raw;
    }
    return result;
}
/**
 * Offset of `timeZone` from UTC, in minutes, at a specific instant. Positive is
 * east of UTC. Instant-specific because the answer changes across a DST
 * boundary - a September appointment and a December appointment in Toronto do
 * not have the same offset.
 */
export function offsetMinutes(instant, timeZone) {
    const date = instant instanceof Date ? instant : new Date(instant);
    const p = partsInZone(date, timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    // Seconds are the finest granularity Intl gives back, so drop the
    // milliseconds from both sides before differencing.
    const actual = Math.floor(date.getTime() / 1000) * 1000;
    return Math.round((asUtc - actual) / 60_000);
}
export function formatOffset(minutes) {
    const sign = minutes < 0 ? "-" : "+";
    const abs = Math.abs(minutes);
    const hours = String(Math.floor(abs / 60)).padStart(2, "0");
    const mins = String(abs % 60).padStart(2, "0");
    return `UTC${sign}${hours}:${mins}`;
}
/**
 * Renders an instant for a human in a given zone, with the zone spelled out.
 * Every appointment time shown to a user carries its zone - "14:00" alone is
 * an invitation for someone to miss a consultation by five hours.
 */
export function formatInZone(instant, timeZone, locale = "en-CA") {
    const date = instant instanceof Date ? instant : new Date(instant);
    const dateText = new Intl.DateTimeFormat(locale, {
        timeZone,
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    }).format(date);
    const timeText = new Intl.DateTimeFormat(locale, {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(date);
    const abbreviation = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value ?? timeZone;
    return {
        date: dateText,
        time: timeText,
        zoneAbbreviation: abbreviation,
        offset: formatOffset(offsetMinutes(date, timeZone)),
    };
}
/** e.g. "Monday, 14 September 2026 at 09:30 EDT (UTC-04:00)" */
export function formatFull(instant, timeZone, locale = "en-CA") {
    const d = formatInZone(instant, timeZone, locale);
    return `${d.date} at ${d.time} ${d.zoneAbbreviation} (${d.offset})`;
}
/**
 * How far apart two participants are at a given instant. Used to warn a
 * coordinator when they are about to book a specialist at 03:00 their time.
 */
export function hoursApart(instant, zoneA, zoneB) {
    return (offsetMinutes(instant, zoneA) - offsetMinutes(instant, zoneB)) / 60;
}
/** True when the local hour in `timeZone` falls outside normal working hours. */
export function isUnsociableHour(instant, timeZone, { from = 7, to = 21 } = {}) {
    const date = instant instanceof Date ? instant : new Date(instant);
    const { hour } = partsInZone(date, timeZone);
    return hour < from || hour >= to;
}
//# sourceMappingURL=time.js.map